import { useNotification } from 'app/providers'
import {
  useGetCategoryColorsQuery,
  useResetCategoryColorsMutation,
  useUpdateCategoryColorsMutation,
} from 'features/SettingsOld/state/api/settingsApi'
import styles from './configurator.module.scss'
import { generateBackgroundColor } from './helpers'

import { Icon } from '@iconify/react'
import { Col, InputNumber, Modal, Row, Spin } from 'antd'
import { Button } from 'common/components/Button/Button'
import Popup from 'common/components/Popup'
import Show from 'common/components/Show2/Show'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BUTTON_CONSTANTS, BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { useFormik } from 'formik'
import { ColorConfiguratorPicker } from './ColorPicker'

type Props = {
  open: boolean
  onClose: () => void
}

export const CategoryColorConfigurator = ({ open, onClose }: Props) => {
  const { data, isFetching } = useGetCategoryColorsQuery()
  const { setNotification } = useNotification()

  const [updateCategoryColors, { isLoading: isUpdating }] = useUpdateCategoryColorsMutation()
  const [resetCategoryColors, { isLoading: isResetting }] = useResetCategoryColorsMutation()

  const { values, dirty, resetForm, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      categoryColors: data,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const resp = await updateCategoryColors(values.categoryColors).unwrap()
      if (resp.status) {
        setNotification({
          description: resp.message,
          type: ALERT_CONSTANTS.SUCCESS,
        })
        onClose()
      } else {
        setNotification({
          description: resp.message,
          type: ALERT_CONSTANTS.ERROR,
        })
      }
    },
  })

  const handleColorChange = (color: string, index: number) => {
    const newCategoryColors = [...values.categoryColors]
    const background = generateBackgroundColor(color)
    newCategoryColors[index] = {
      ...newCategoryColors[index],
      color,
      background,
    }
    setFieldValue('categoryColors', newCategoryColors)
  }

  const handleDurationChange = (duration: number, index: number) => {
    const newCategoryColors = [...values.categoryColors]
    newCategoryColors[index] = {
      ...newCategoryColors[index],
      duration,
    }
    setFieldValue('categoryColors', newCategoryColors)
  }

  const handleResetColors = () => {
    Popup.showInfo(
      {
        title: 'Are you sure?',
        message:
          'You want to reset the category colors to Residen default? This action cannot be undone.',
        mainButtonText: 'Yes',
        secondButtonText: 'Cancel',
      },
      () => handleResetCategoryColors(),
    )
  }

  const handleResetCategoryColors = async () => {
    const resp = await resetCategoryColors().unwrap()
    if (resp.status) {
      setNotification({
        description: resp.message,
        type: ALERT_CONSTANTS.SUCCESS,
      })
      onClose()
      resetForm()
    } else {
      setNotification({
        description: resp.message,
        type: ALERT_CONSTANTS.ERROR,
      })
    }
  }

  return (
    <>
      <Modal
        footer={null}
        maskClosable={false}
        open={open}
        onCancel={onClose}
        classNames={{ content: styles.content }}>
        <div className={styles.container}>
          <h3 className={styles.title}>Category Colors</h3>
          <Show>
            <Show.When isVisible={isFetching}>
              <div className={styles.loadingContainer}>
                <Spin />
              </div>
            </Show.When>
            <Show.Else>
              <Spin spinning={isUpdating || isResetting}>
                <form
                  id='category-color-configurator'
                  onSubmit={handleSubmit}
                  className={styles.colorContainer}>
                  {values.categoryColors?.map(
                    ({ color, background, name, code, duration }, index) => {
                      return (
                        <div key={code} className={styles.categoryColorItem}>
                          <ColorConfiguratorPicker
                            value={color}
                            defaultColor={color}
                            onChange={(newColor) => handleColorChange(newColor, index)}>
                            <div
                              data-updating={isUpdating}
                              onClick={() => setFieldValue('selectedColorIndex', index)}
                              className={styles.colorItem}
                              style={{ background, color }}>
                              <p className={styles.colorItemName}>{name}</p>
                              <Icon icon='ph:pencil-simple' width={24} height={24} />
                            </div>
                          </ColorConfiguratorPicker>
                          <div className={styles.durationContainer}>
                            <label className={styles.durationLabel}>Duration (minutes):</label>
                            <InputNumber
                              size='small'
                              min={1}
                              max={999}
                              value={duration}
                              onChange={(value) => handleDurationChange(value || 15, index)}
                              className={styles.durationInput}
                            />
                          </div>
                        </div>
                      )
                    },
                  )}
                </form>
              </Spin>
              <Row justify='space-between' gutter={[16, 16]}>
                <Col>
                  <Button
                    disabled={isUpdating || isResetting}
                    modifier={BUTTON_MODIFIER.SECONDARY}
                    onClick={() => {
                      resetForm()
                      onClose()
                    }}>
                    {BUTTON_CONSTANTS.CANCEL}
                  </Button>
                </Col>
                <Col>
                  <Button
                    htmlType='button'
                    disabled={isUpdating || isResetting}
                    modifier={BUTTON_MODIFIER.TERTIARY}
                    onClick={handleResetColors}>
                    Reset
                  </Button>
                </Col>
                <Col>
                  <Button
                    disabled={!dirty || isUpdating || isResetting}
                    htmlType='submit'
                    form='category-color-configurator'>
                    {BUTTON_CONSTANTS.SUBMIT}
                  </Button>
                </Col>
              </Row>
            </Show.Else>
          </Show>
        </div>
      </Modal>
    </>
  )
}
